import { SensitiveFileFolder } from 'lib/types/s3-files';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { S3SensitiveFileUploader } from '../helpers';
import { MultiFileUpload } from './MultiFileUpload';

interface IS3MultiFileUploadProps {
  accept: string;
  companyUrlAlias: string;
  files?: File[];
  folder: SensitiveFileFolder;
  isDisabled?: boolean;
  label: string;
  maxFileSizeMB: number;
  onLoading?: () => void;
  onLoadingComplete?: () => void;
  onRemove: (sensitiveFileId: number, file: File) => void;
  onSuccess: (sensitiveFileId: number, file: File) => void;
  setHasErrors: (hasErrors: boolean) => void;
}

export const S3MultiFileUpload = ({
  accept,
  companyUrlAlias,
  folder,
  isDisabled = false,
  label,
  maxFileSizeMB,
  onLoading = () => null,
  onLoadingComplete = () => null,
  onRemove,
  onSuccess,
  setHasErrors,
}: IS3MultiFileUploadProps) => {
  const uploaderRefs = useRef({});
  const [files, setFiles] = useState<File[]>([]);
  const [uploadPercentages, setUploadPercentages] = useState<{
    [key: string]: number;
  }>({});
  const [filesWithErrors, setFilesWithErrors] = useState<
    { fileName: string; message?: string }[]
  >([]);

  useEffect(() => {
    setHasErrors(!_.isEmpty(filesWithErrors));
  }, [filesWithErrors]);

  const handleChangeFiles = async (changedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...changedFiles]);
    const newUploaders = [];

    changedFiles.forEach((changedFile) => {
      const uploader = (uploaderRefs.current[changedFile.name] =
        new S3SensitiveFileUploader({
          file: changedFile,
          folder,
          accept,
          companyUrlAlias,
        }));

      uploader.onAbort = () => {
        setFiles((prevFiles) =>
          _.reject(prevFiles, (file) => file.name === changedFile.name)
        );
        setUploadPercentages(_.omit(uploadPercentages, changedFile.name));
        onLoadingComplete();
      };

      uploader.onError = (message?: string) => {
        setFiles((prevFiles) =>
          _.reject(prevFiles, (file) => file.name === changedFile.name)
        );
        setFilesWithErrors((prevFilesWithErrors) => [
          ...prevFilesWithErrors,
          { fileName: changedFile.name, message },
        ]);
        setUploadPercentages((prevUploadPercentages) =>
          _.omit(prevUploadPercentages, changedFile.name)
        );
        onLoadingComplete();
      };

      uploader.onProgress = (percentComplete: number) => {
        onLoading();
        setFilesWithErrors((prevFilesWithErrors) =>
          _.reject(prevFilesWithErrors, (x) => x.fileName === changedFile.name)
        );
        setUploadPercentages((prevUploadPercentages) => ({
          ...prevUploadPercentages,
          [changedFile.name]: percentComplete,
        }));
      };

      uploader.onSuccess = ({ fileId }) => {
        onLoadingComplete();
        onSuccess(fileId, changedFile);
      };

      newUploaders.push(uploader);
    });

    await Promise.all(newUploaders.map((uploader) => uploader.uploadFile()));
  };

  const handleRemoveFile = (name: string) => {
    const uploader = uploaderRefs.current[name];
    if (uploader) {
      const sensitiveFileId = uploader.fileId;
      const file = _.find(files, (file) => file.name === name);
      setFiles(_.reject(files, (file) => file.name === name));
      uploaderRefs.current = _.omit(uploaderRefs.current, name);
      onRemove(sensitiveFileId, file);
    }
    setFilesWithErrors(_.reject(filesWithErrors, (x) => x.fileName === name));
  };

  return (
    <MultiFileUpload
      accept={accept}
      filesWithErrors={filesWithErrors}
      isDisabled={isDisabled}
      label={label}
      maxFileSizeMB={maxFileSizeMB}
      onChange={handleChangeFiles}
      onRemove={handleRemoveFile}
      setFilesWithError={setFilesWithErrors}
      uploadPercentages={uploadPercentages}
    />
  );
};
